import {EUR, USD} from '~/entities/currencies';
import {AccountId} from '~/entities/operations-fiat';
import {ExchangeFiatProps} from "../../ui/Exchange/ExchangeFiat";
import {FinancialData} from '~/redux/finances';
import {groupOperationsByType} from '~/redux/finances/financesPrimitives';
import {invariant} from '~/shared/utils/types';
import {makeBareAccount} from '../AccountAdapters';
import {hasAccountOfType, hasTargetOfType, isEveryOf, uniqueById} from '../SelectorTools';

export const selectExchangeData =
    ({accountId, targetId}: {accountId: AccountId; targetId: AccountId}) =>
    (data: FinancialData): ExchangeFiatProps => {
        {
            const fiatExchangeOps = groupOperationsByType(data).exchange.filter(
                isEveryOf(hasAccountOfType('fiat'), hasTargetOfType('fiat'))
            );

            const maybeAccount = fiatExchangeOps.find(op => op.account.id === accountId)?.account;
            invariant(!!maybeAccount, `Account with id ${accountId} is not found`);

            const thisProviderTopupOps = fiatExchangeOps.filter(
                op => op.account.providerId === maybeAccount.providerId
            );

            const provider = data.providers.find(p => p.id === maybeAccount.providerId);

            const fromAccounts = uniqueById(thisProviderTopupOps.map(op => makeBareAccount(op.account)));
            const toAccounts = uniqueById(thisProviderTopupOps.map(op => makeBareAccount(op.target)));
            const fromAccount = makeBareAccount(maybeAccount);
            const toAccount = toAccounts.find(ac => ac.id === targetId);

            invariant(!!fromAccount, `Account with id ${JSON.stringify(accountId)} not found`);
            invariant(!!toAccount, `Account with id ${JSON.stringify(targetId)} not found`);
            const getSettings = settingsGetterMap[fromAccount.currency][toAccount.currency];

            return {
                providerName: provider?.name,
                fromAccount,
                fromAccounts,
                toAccount,
                toAccounts,
                settings: getSettings(data.operationsSettings),
            };
        }
    };

const settingsGetterMap = {
    [USD]: {
        [EUR]: (settings: FinancialData['operationsSettings']) => ({
            exchangeRate: settings.usd_exchange_eur_rate,
            minimal: settings.usd_exchange_eur_minimal,
        }),
    },
    [EUR]: {
        [USD]: (settings: FinancialData['operationsSettings']) => ({
            exchangeRate: settings.eur_exchange_usd_rate,
            minimal: settings.eur_exchange_usd_minimal,
        }),
    },
};
